import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Radio } from 'lucide-react';
import { wbApi, fmt, dataHoraBr } from './wbApi';

const PAGE = 50;
const ABAS = [
  { id: 'WIN_SMALL', label: '🎃 Prêmios 1.000', on: 'bg-lime-500/25 border-lime-400 text-lime-100', off: 'text-lime-300/60 hover:border-lime-400/50' },
  { id: 'WIN_BIG', label: '👑 Balão Dourado 50.000', on: 'bg-amber-500/25 border-amber-400 text-amber-100', off: 'text-amber-300/60 hover:border-amber-400/50' },
];

const dedupe = (list) => {
  const seen = new Set();
  return list.filter((w) => { if (!w.pop_id || seen.has(w.pop_id)) return false; seen.add(w.pop_id); return true; });
};

/* Histórico AO VIVO de prêmios (igual à Roleta): uma aba por faixa de prêmio; balões vazios nunca aparecem.
   Carrega do servidor em páginas (rolagem infinita) e recebe novos vencedores pelo WebSocket. */
export const WbFeed = ({ feed = [] }) => {
  const [aba, setAba] = useState('WIN_SMALL');
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const abaRef = useRef('WIN_SMALL');
  const loadingRef = useRef(false);

  const load = useCallback(async (offset, result) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const { data } = await wbApi.get('/winners', { params: { result, limit: PAGE, offset } });
      if (abaRef.current === result) {
        setRows((prev) => dedupe(offset === 0 ? (data.winners || []) : [...prev, ...(data.winners || [])]));
        setHasMore(!!data.has_more);
      }
    } catch (e) { /* silencioso: tenta de novo na próxima rolagem */ }
    loadingRef.current = false;
    setCarregando(false);
  }, []);

  useEffect(() => { load(0, 'WIN_SMALL'); }, [load]);

  useEffect(() => {
    const novos = feed.filter((f) => (f.type === 'wb.win_small' || f.type === 'wb.win_big') && f.data?.pop_id && f.data?.result === abaRef.current)
      .map((f) => ({ pop_id: f.data.pop_id, nickname: f.data.nickname, avatar: f.data.avatar || '', prize: Number(f.data.prize || 0), result: f.data.result, at: f.at }));
    if (novos.length) setRows((prev) => dedupe([...novos, ...prev]));
  }, [feed]);

  const trocar = (id) => {
    if (id === abaRef.current) return;
    abaRef.current = id;
    setAba(id); setRows([]); setHasMore(false); setCarregando(true);
    load(0, id);
  };

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 60) load(rows.length, abaRef.current);
  };

  return (
    <div className="wb-card px-4 py-3" data-testid="wb-feed-card">
      <div className="flex items-center gap-2">
        <Radio className="h-3.5 w-3.5 animate-pulse text-red-400" />
        <p className="text-[10px] font-black uppercase tracking-[.2em] text-purple-200/90">Prêmios ao vivo</p>
      </div>
      <div className="mt-2 flex gap-1.5">
        {ABAS.map((a) => (
          <button key={a.id} onClick={() => trocar(a.id)} data-testid={`wb-winners-tab-${a.id === 'WIN_SMALL' ? 'small' : 'big'}`}
            className={`flex-1 rounded-lg border px-2 py-1.5 text-[11px] font-black transition-colors ${aba === a.id ? a.on : `border-white/10 bg-white/5 ${a.off}`}`}>
            {a.label}
          </button>
        ))}
      </div>
      <ul className="wb-feed-scroll mt-2 max-h-[260px] space-y-1.5 overflow-y-auto overscroll-contain pr-1" data-testid="wb-feed-list" onScroll={onScroll}>
        {!carregando && rows.length === 0 && (
          <li className="py-3 text-center text-[11px] text-purple-100/60" data-testid="wb-winners-empty">
            {aba === 'WIN_BIG' ? 'Ninguém levou o Balão Dourado ainda — pode ser você! 👑' : 'Nenhum prêmio ainda — estoure um balão e apareça aqui! 🎃'}
          </li>
        )}
        {rows.map((w) => (
          <li key={w.pop_id} data-testid="wb-feed-item"
            className={`wb-feed-item flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${w.result === 'WIN_BIG' ? 'border-amber-500/40 bg-amber-500/10' : 'border-white/10 bg-white/5'}`}>
            {w.avatar ? <img src={w.avatar} alt="" className="h-7 w-7 shrink-0 rounded-full border border-purple-400/40 object-cover" />
              : <span className="h-7 w-7 shrink-0 rounded-full bg-purple-800/60" />}
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-bold text-white">{w.nickname}</p>
              <p className="text-[10px] text-purple-300/70" data-testid="wb-feed-item-date">{dataHoraBr(w.at)}</p>
            </div>
            <span className={`shrink-0 text-[12px] font-black ${w.result === 'WIN_BIG' ? 'text-amber-300' : 'text-lime-300'}`}>+{fmt(w.prize)}</span>
          </li>
        ))}
        {hasMore && <li className="py-1 text-center text-[10px] text-purple-300/60">role para carregar mais…</li>}
      </ul>
    </div>
  );
};
